import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useDesign } from '../../designs';
import { ItineraryItem, TripLodging } from '../../types/trip';
import { formatTime12h } from '../../components/ui/TimeSpinner';
import { loadMapsApi } from '../../components/ui/PlacesAutocomplete';
import { formatDayHeader } from '../../utils/dates';
import { parseStreetAddress } from '../../utils/address';

interface MapViewProps {
  items: ItineraryItem[];
  lodging?: TripLodging;
}

interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  label: string;
  color: string;
  name: string;
  address: string;
  isLodging: boolean;
  status?: string;
}

function mapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/** Dark map style that complements the amethyst Parlor UI */
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1d1d2e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6a6a8a' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#2a2a40' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a42' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1f1f35' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#353550' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#111122' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a4a6a' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a2a1a' }, { visibility: 'on' }] },
];

export default function MapView({ items, lodging }: MapViewProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<Map<string, any>>(new Map());
  const infoWindowRef = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');

  // Collect all points with coordinates
  const points = useMemo(() => {
    const pts: MapPoint[] = [];
    if (lodging?.lat && lodging?.lng) {
      pts.push({
        id: 'lodging', lat: lodging.lat, lng: lodging.lng, label: '★',
        color: d.textPrimary, name: lodging.name || 'Lodging',
        address: lodging.address || '', isLodging: true,
      });
    }
    items.forEach((item, i) => {
      if (item.lat && item.lng) {
        pts.push({
          id: item.itemId, lat: item.lat, lng: item.lng,
          label: String(i + 1),
          color: item.status === 'confirmed' ? d.accent : d.textTertiary,
          name: item.name || '', address: item.address || '',
          isLodging: false, status: item.status,
        });
      }
    });
    return pts;
  }, [items, lodging, d.accent, d.textTertiary, d.textPrimary]);

  const hasMapData = points.length > 0;

  // Open InfoWindow for a point
  const openInfoWindow = useCallback((pointId: string) => {
    const marker = markersRef.current.get(pointId);
    const map = mapInstanceRef.current;
    const point = points.find((p) => p.id === pointId);
    if (!marker || !map || !point) return;

    const w = window as any;
    if (!w.google?.maps) return;

    if (infoWindowRef.current) infoWindowRef.current.close();

    const shortAddr = parseStreetAddress(point.address);
    const link = mapsUrl(point.lat, point.lng);
    const content = `<div style="font-family:'Raleway',sans-serif;padding:4px 2px;max-width:220px;">` +
      `<div style="font-size:14px;font-weight:600;color:#1a1a2e;margin-bottom:2px;">${point.name}</div>` +
      (shortAddr ? `<div style="font-size:12px;color:#666;margin-bottom:6px;">${shortAddr}</div>` : '') +
      `<a href="${link}" target="_blank" rel="noopener noreferrer" style="font-size:11px;color:#C8923A;text-decoration:none;font-weight:500;">Open in Google Maps →</a>` +
      `</div>`;

    const iw = new w.google.maps.InfoWindow({ content });
    iw.open(map, marker);
    infoWindowRef.current = iw;

    map.panTo({ lat: point.lat, lng: point.lng });
  }, [points]);

  // Load Google Maps API proactively
  useEffect(() => {
    if (!hasMapData || !mapRef.current) return;
    const w = window as any;
    if (w.google?.maps) { setMapReady(true); return; }

    setMapError('Map loading — please wait...');
    loadMapsApi()
      .then(() => { setMapReady(true); setMapError(''); })
      .catch(() => { setMapError('Map unavailable — Google Maps API not loaded'); });
  }, [hasMapData]);

  // Render map
  useEffect(() => {
    if (!mapReady || !mapRef.current || !hasMapData) return;
    const w = window as any;
    const google = w.google;
    if (!google?.maps) return;

    try {
      const bounds = new google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));

      const map = new google.maps.Map(mapRef.current, {
        center: bounds.getCenter(),
        zoom: 13,
        disableDefaultUI: true,
        zoomControl: true,
        styles: DARK_MAP_STYLES,
        backgroundColor: '#1d1d2e',
      });
      mapInstanceRef.current = map;

      if (points.length > 1) map.fitBounds(bounds, 40);

      // Clear old markers
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current.clear();

      points.forEach((p) => {
        const marker = new google.maps.Marker({
          position: { lat: p.lat, lng: p.lng },
          map,
          label: {
            text: p.label,
            color: p.isLodging ? '#130E1C' : '#FFFFFF',
            fontSize: p.isLodging ? '8px' : '10px',
            fontWeight: 'bold',
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: p.color,
            fillOpacity: 1,
            strokeColor: 'rgba(255,255,255,0.25)',
            strokeWeight: 1.5,
          },
        });
        markersRef.current.set(p.id, marker);
        marker.addListener('click', () => openInfoWindow(p.id));
      });
    } catch (err) {
      console.error('Failed to render map:', err);
      setMapError('Map unavailable — rendering error');
    }
  }, [mapReady, hasMapData, points, openInfoWindow]);

  return (
    <div style={{ padding: '0 16px' }}>
      {/* Map container */}
      {hasMapData && !mapError ? (
        <div ref={mapRef} style={{
          width: '100%', height: '260px', borderRadius: '2px',
          marginBottom: '12px', border: '1px solid rgba(200,146,58,0.10)',
        }} />
      ) : (
        <div style={{
          width: '100%', height: '200px', borderRadius: '2px',
          backgroundColor: d.bgSurface, border: '1px solid rgba(200,146,58,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '12px',
        }}>
          <p style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
            color: d.textSecondary, padding: '0 16px', textAlign: 'center',
          }}>
            {mapError || 'Add addresses to activities to see them on the map'}
          </p>
        </div>
      )}

      {/* Legend */}
      <div style={{
        display: 'flex', gap: '16px', marginBottom: '16px', justifyContent: 'center', alignItems: 'center',
      }}>
        {[
          { color: d.accent, label: 'Confirmed' },
          { color: d.textTertiary, label: 'Considering' },
          { color: d.textPrimary, label: 'Lodging' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{
              display: 'inline-block', width: '8px', height: '8px',
              borderRadius: '50%', backgroundColor: color, flexShrink: 0,
            }} />
            <span style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 400,
              color: d.textSecondary,
            }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Item list — only items with coordinates (matching map pins) */}
      {lodging?.name && lodging?.lat && (
        <div
          onClick={() => openInfoWindow('lodging')}
          style={{
            display: 'flex', alignItems: 'center', padding: '10px 0',
            borderBottom: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer',
          }}
        >
          <span style={{
            display: 'inline-block', width: '6px', height: '6px',
            borderRadius: '50%', backgroundColor: d.textPrimary,
            marginRight: '10px', flexShrink: 0,
          }} />
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.itemName, fontWeight: 500,
            color: d.textPrimary, flex: 1,
          }}>
            {lodging.name}
          </span>
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 400,
            color: d.textSecondary, flexShrink: 0,
          }}>
            Lodging
          </span>
        </div>
      )}
      {(() => {
        const mappedItems = items.filter((item) => item.lat && item.lng);
        // Group by day
        const byDay: Record<string, ItineraryItem[]> = {};
        const undated: ItineraryItem[] = [];
        mappedItems.forEach((item) => {
          if (item.day) {
            if (!byDay[item.day]) byDay[item.day] = [];
            byDay[item.day].push(item);
          } else {
            undated.push(item);
          }
        });
        const sortedDays = Object.keys(byDay).sort();
        return (
          <>
            {sortedDays.map((day) => {
              const { dayName } = formatDayHeader(day);
              const dateLabel = new Date(day + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const dayItems = byDay[day].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
              return (
                <React.Fragment key={day}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '12px 0 6px',
                  }}>
                    <span style={{
                      fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 700,
                      letterSpacing: '2.5px', textTransform: 'uppercase', color: d.textSecondary,
                    }}>{dayName}</span>
                    <span style={{
                      fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 300, color: d.textTertiary,
                    }}>{dateLabel}</span>
                  </div>
                  {dayItems.map((item) => {
                    const isConfirmed = item.status === 'confirmed';
                    return (
                      <div key={item.itemId} onClick={() => openInfoWindow(item.itemId)} style={{
                        display: 'flex', alignItems: 'center', padding: '9px 0',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        cursor: 'pointer', opacity: isConfirmed ? 1 : 0.85,
                      }}>
                        <span style={{
                          display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%',
                          backgroundColor: isConfirmed ? d.accent : d.textTertiary,
                          marginRight: '10px', flexShrink: 0,
                        }} />
                        <span style={{
                          fontFamily: "'Raleway', sans-serif", fontSize: fs.itemName, fontWeight: 500,
                          color: d.textPrimary, flex: 1, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {item.name || ''}
                        </span>
                        {item.time && (
                          <span style={{
                            fontFamily: displayFont, fontSize: fs.bodyInput, fontWeight: 700,
                            color: d.accent, flexShrink: 0, marginLeft: '8px',
                          }}>
                            {formatTime12h(item.time)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {undated.length > 0 && (
              <>
                <div style={{ padding: '12px 0 6px' }}>
                  <span style={{
                    fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 700,
                    letterSpacing: '2.5px', textTransform: 'uppercase', color: d.textTertiary,
                  }}>Unscheduled</span>
                </div>
                {undated.map((item) => {
                  const isConfirmed = item.status === 'confirmed';
                  return (
                    <div key={item.itemId} onClick={() => openInfoWindow(item.itemId)} style={{
                      display: 'flex', alignItems: 'center', padding: '9px 0',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                      cursor: 'pointer', opacity: isConfirmed ? 1 : 0.85,
                    }}>
                      <span style={{
                        display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%',
                        backgroundColor: isConfirmed ? d.accent : d.textTertiary,
                        marginRight: '10px', flexShrink: 0,
                      }} />
                      <span style={{
                        fontFamily: "'Raleway', sans-serif", fontSize: fs.itemName, fontWeight: 500,
                        color: d.textPrimary, flex: 1, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {item.name || ''}
                      </span>
                    </div>
                  );
                })}
              </>
            )}
          </>
        );
      })()}

      {items.filter((i) => i.lat && i.lng).length === 0 && !lodging?.lat && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
          color: d.textTertiary, textAlign: 'center', padding: '24px',
        }}>
          No items to show on map
        </p>
      )}
    </div>
  );
}
